import React from 'react';
import * as R from 'ramda';
import { NavLink } from 'react-router-dom';
import keycode from 'keycode';
import { Button, Icon } from '@econ/ui-elements';
import Agreement from './Agreement';
import Text, { text as textById } from '../../../shared/components/l10n/Text';
import { withEContext, EContextProps } from '../../../shared/components/EContext';

const sortByAgreementName = R.sortBy(R.compose(R.toLower, R.prop('companyName')));

interface Props {
    agreements: {
        agreementNumber: string;
        companyName: string;
        userId: string;
        isDeveloper: boolean;
        isAdministrator: boolean;
        hasCompany2FaEnforced: boolean;
        clients: {
            agreementNumber: string;
            companyName: string;
            isDeveloper: boolean;
            hasCompany2FaEnforced: boolean;
        }[];
    }[];
    context: EContextProps;
    onChangeAgreement: Function;
    refetchAgreements: Function;
    session: any;
}

interface State {
    searchValue: string;
}

class AgreementSelector extends React.Component<Props, State> {
    state = {
        searchValue: '',
    };

    render() {
        const {
            agreements = [],
            context: { identity, texts },
            session,
        } = this.props;
        const { searchValue } = this.state;

        return (
            <div id="agreement_selector">
                <div id="agreement_selector__search">
                    <label>
                        <input
                            autoFocus
                            onChange={this.handleSearch}
                            placeholder={textById(texts)('Search for agreement...', 'AGREEMENT_SELECTOR_SEARCH_PLACEHOLDER')}
                            type="search"
                            value={searchValue}
                        />
                        <Icon name="search" />
                    </label>
                </div>
                <div id="agreement_selector__agreements">
                    {sortByAgreementName(agreements)
                        .filter(this.filterAgreement)
                        .map(agreement =>
                            agreement.clients ? (
                                <div className="agreement_details__administrator" key={agreement.agreementNumber}>
                                    {this.filterBySearchValue(agreement) && (
                                        <Agreement
                                            name={agreement.companyName}
                                            number={agreement.agreementNumber}
                                            isAdministrator={agreement.isAdministrator}
                                            isDeveloper={agreement.isDeveloper}
                                            userId={agreement.userId}
                                            enforce2Fa={
                                                agreement.hasCompany2FaEnforced && session.hasVismaConnect && !session.with2Fa
                                            }
                                            onClick={() => this.props.onChangeAgreement(agreement)}
                                        />
                                    )}
                                    <div className="agreement_details__administrator__clients">
                                        {sortByAgreementName(agreement.clients)
                                            .filter(this.filterClientAgreement)
                                            .map(client => (
                                                <Agreement
                                                    key={client.agreementNumber}
                                                    isDeveloper={client.isDeveloper}
                                                    name={client.companyName}
                                                    userId={agreement.userId}
                                                    number={client.agreementNumber}
                                                    enforce2Fa={
                                                        client.hasCompany2FaEnforced &&
                                                        session.hasVismaConnect &&
                                                        !session.with2Fa
                                                    }
                                                    onClick={() => this.props.onChangeAgreement(client)}
                                                />
                                            ))}
                                    </div>
                                </div>
                            ) : (
                                <Agreement
                                    key={agreement.agreementNumber}
                                    name={agreement.companyName}
                                    number={agreement.agreementNumber}
                                    userId={agreement.userId}
                                    isAdministrator={agreement.isAdministrator}
                                    isDeveloper={agreement.isDeveloper}
                                    enforce2Fa={agreement.hasCompany2FaEnforced && session.hasVismaConnect && !session.with2Fa}
                                    onClick={() => this.props.onChangeAgreement(agreement)}
                                />
                            )
                        )}
                </div>

                {identity!.user.vismaId && (
                    <Button id="agreement_selector__add_account" onClick={this.handleAddAccount}>
                        <Text id="AGREEMENT_SELECTOR_ADD_ACCOUNT">Add agreement</Text>
                    </Button>
                )}

                <NavLink id="agreement_selector__logout" to={'/logout'}>
                    <Text id="AGREEMENT_SELECTOR_LOG_OUT">Log out</Text>
                </NavLink>
            </div>
        );
    }

    handleAddAccount = () => {
        // until we have a form system we call legacy e-conomic, make sure it's available before doing that (for the fast clickers)
        if (window.top.Econ && window.top.Econ.modalWithEnforcedJsx) {
            window.top.Econ.modalWithEnforcedJsx('tmpl/users/add-account', {
                onSuccess: this.props.refetchAgreements,
            });
        } else {
            window.top.addEventListener('firebird:ready', () =>
                window.top.Econ.modalWithEnforcedJsx('tmpl/users/add-account', {
                    onSuccess: this.props.refetchAgreements,
                })
            );
        }
    };

    handleSearch = event => {
        if (event.keyCode === keycode(',')) return;
        this.setState({ searchValue: event.target.value });
    };

    filterAgreement = agreement =>
        agreement.statuses.length === 0 && (!!agreement.clients || this.filterBySearchValue(agreement));

    filterClientAgreement = agreement => {
        var searchValue = this.state.searchValue;

        if (agreement.statuses.length === 0 && (agreement.favorite || searchValue.length >= 3)) {
            return this.filterBySearchValue(agreement);
        }

        return false;
    };

    filterBySearchValue = agreement => {
        var searchValue = this.state.searchValue;

        if (!searchValue || !searchValue.length) return true;

        var matchesAgreementNumber = String(agreement.agreementNumber).indexOf(searchValue) > -1;
        var matchesCompanyName = agreement.companyName.toLowerCase().indexOf(searchValue.toLowerCase()) > -1;

        return matchesAgreementNumber || matchesCompanyName;
    };
}

export default withEContext(AgreementSelector);
